import {
  Button,
  DialogContent,
  DialogTitle,
  Modal,
  ModalClose,
  ModalDialog,
  DialogActions,
  Stack,
  Box,
  Select,
  Typography,
  Option,
  IconButton,
  Input,
} from "@mui/joy";
import { useTranslation } from "../../../i18n";
import {
  useContactUpsertMutation,
  useTableViewContactsQuery,
} from "../../../__generated__/types-and-hooks";
import { ReactNode, useCallback, useEffect, useState } from "react";
import { useTeam } from "../TeamLayout";
import { useNotify } from "../../../hooks/useNotify";
import { Contact$ } from "./Contact$";
import { XTextInput } from "../../../x-components/XTextInput";
import Email from "@mui/icons-material/EmailOutlined";
import WhatsApp from "@mui/icons-material/WhatsApp";
import Phone from "@mui/icons-material/LocalPhone";
import Message from "@mui/icons-material/Message";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

export interface ContactAddModalProps {
  open: boolean;
  onClose: () => void;
}

const iconMap: Record<string, ReactNode> = {
  phone: <Phone fontSize="small" />,
  email: <Email fontSize="small" />,
  whatsapp: <WhatsApp fontSize="small" />,
  other: <Message fontSize="small" />,
};

export default function ContactAddModal({
  open,
  onClose,
}: ContactAddModalProps) {
  const teamCtx = useTeam();
  const [contact$, setContact$] = useState<Contact$>(() => new Contact$());
  const [contactMethods, setContactMethods] = useState<
    { key: string; value: string }[]
  >([]);
  if (contactMethods.length === 0) {
    contactMethods.push({ key: "phone", value: "" });
  }
  const handleChange = (index: number, key: string, value: string) => {
    const updatedMethods = [...contactMethods];
    (updatedMethods[index] as any)[key] = value;
    setContactMethods(updatedMethods);
  };
  const handleRemove = (index: number) => {
    const arr = [...contactMethods];
    arr.splice(index, 1);
    setContactMethods(arr);
  };
  const handleAdd = () => {
    const arr = [...contactMethods];
    arr.push({ key: "phone", value: "" });
    setContactMethods(arr);
  };

  useEffect(() => {
    setContact$(new Contact$());
  }, [open]);

  const t = useTranslation();
  const [contactUpsertMutation, { loading }] = useContactUpsertMutation();
  const contactsListQuery = useTableViewContactsQuery({
    variables: { teamId: teamCtx.team.id },
  });

  const [saving, setSaving] = useState(false);
  const notify = useNotify();

  const handleSave = useCallback(async () => {
    setSaving(true);
    contact$.json$
      .subscribe({
        next: async (data) => {
          setSaving(true);

          try {
            const res = await contactUpsertMutation({
              variables: {
                contact: {
                  name: data.fullName,
                  teamId: teamCtx.team.id,
                  contactMethods,
                },
              },
              onCompleted() {
                contactsListQuery.refetch();
              },
            });

            setSaving(false);
            onClose();
            if (res.errors) {
              notify({ type: "danger", msg: "An error has been occurred." });
            } else {
              notify({
                type: "success",
                msg: "New SKU has been created.",
              });
            }
          } catch (e) {
            console.error(e);
            notify({ type: "danger", msg: "An error has been occurred." });
          }
        },
      })
      .unsubscribe();
  }, [
    contact$,
    notify,
    contactsListQuery,
    contactUpsertMutation,
    teamCtx,
    onClose,
    contactMethods,
  ]);

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog size="md" variant="outlined">
        <ModalClose />
        <DialogTitle>{t("Create New Contact")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ minHeight: 300 }}>
            <XTextInput
              placeholder="e.g. John Doe"
              val$={contact$.fullName$}
              label={t("Full Name")}
            />
            <Typography
              level="body-xs"
              sx={{ marginTop: 1, wordWrap: "break-word" }}
            >
              {t(
                "Use the dropdown to select the type (e.g., phone, email, WhatsApp) and provide the relevant details."
              )}
              <br />
              {t(
                'Add new methods as needed, or click "Remove" to delete existing ones.'
              )}
            </Typography>
            {contactMethods.map((method, index) => (
              <Box
                key={index}
                sx={{ display: "flex", alignItems: "center", mb: 2 }}
              >
                <Select
                  startDecorator={
                    iconMap[method.key] ?? <Message fontSize="small" />
                  }
                  value={method.key}
                  onChange={(_e, newValue) =>
                    handleChange(index, "key", newValue!)
                  }
                  sx={{
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                  }}
                  renderValue={(option) => (
                    <Typography
                      sx={{
                        textAlign: "left",
                        width: { xs: "auto", sm: 80 },
                        display: { xs: "none", sm: "block" },
                      }}
                    >
                      {option?.label}
                    </Typography>
                  )}
                >
                  <Option value="phone">
                    <Typography startDecorator={<Phone fontSize="small" />}>
                      {t("Phone")}
                    </Typography>
                  </Option>
                  <Option value="email">
                    <Typography startDecorator={<Email fontSize="small" />}>
                      {t("Email")}
                    </Typography>
                  </Option>
                  <Option value="whatsapp">
                    <Typography startDecorator={<WhatsApp fontSize="small" />}>
                      {t("WhatsApp")}
                    </Typography>
                  </Option>
                  <Option value="other">
                    <Typography startDecorator={<Message fontSize="small" />}>
                      {t("Other")}
                    </Typography>
                  </Option>
                </Select>
                <Input
                  type="text"
                  value={method.value}
                  sx={{
                    flex: 1,
                    borderLeft: 0,
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                  }}
                  onChange={(e) => handleChange(index, "value", e.target.value)}
                  endDecorator={
                    <IconButton color="primary" variant="soft">
                      <ContentCopyIcon
                        onClick={() =>
                          navigator.clipboard.writeText(method.value)
                        }
                      />
                    </IconButton>
                  }
                />
                <Button
                  color="danger"
                  variant="soft"
                  onClick={() => handleRemove(index)}
                  startDecorator={<DeleteOutlineOutlinedIcon />}
                  sx={{
                    ml: 2,
                    display: { xs: "none", sm: "flex" },
                  }}
                >
                  {t("Remove")}
                </Button>
                <IconButton
                  color="danger"
                  variant="soft"
                  onClick={() => handleRemove(index)}
                  sx={{
                    ml: 2,
                    display: { xs: "flex", sm: "none" },
                  }}
                >
                  <DeleteOutlineOutlinedIcon />
                </IconButton>
              </Box>
            ))}
            <Box>
              <Button
                variant="soft"
                onClick={handleAdd}
                startDecorator={<AddIcon />}
              >
                {t("Add")}
              </Button>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            fullWidth
            onClick={handleSave}
            disabled={saving}
            loading={loading}
          >
            {t("Save")}
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
}
